import { memo, useMemo, useCallback } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    Box,
    type SelectChangeEvent,
} from '@mui/material';
import { type GradingStatus } from '../api';
import { MULTI_SELECT_SX } from './filterStyles';
import { toArray } from './filterUtils';

const GRADING_STATUS_OPTIONS: { value: GradingStatus; label: string }[] = [
    { value: 'awaiting_similarity_and_originality', label: 'Awaiting S&O' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'provisional', label: 'Provisional' },
    { value: 'graded', label: 'Graded' },
    { value: 'waived', label: 'Waived' },
];

interface GradingStatusMultiSelectProps {
    /** Selected grading statuses */
    value: GradingStatus[] | undefined;
    /** Callback when selection changes - receives array of statuses or undefined */
    onChange: (statuses: GradingStatus[] | undefined) => void;
    /** Label for the select */
    label?: string;
    /** Component size */
    size?: 'small' | 'medium';
    /** Keep dropdown inside the DOM tree (required when used inside a modal with focus traps) */
    disablePortal?: boolean;
}

function GradingStatusMultiSelectComponent({
    value,
    onChange,
    label = 'Grading Status',
    size = 'small',
    disablePortal,
}: GradingStatusMultiSelectProps) {
    const selectedStatuses = useMemo(() => toArray(value), [value]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<GradingStatus[]>) => {
            const newValue = event.target.value as GradingStatus[];
            onChange(newValue.length > 0 ? newValue : undefined);
        },
        [onChange],
    );

    const handleChipDelete = useCallback(
        (statusToRemove: GradingStatus) => (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            const newStatuses = selectedStatuses.filter(s => s !== statusToRemove);
            onChange(newStatuses.length > 0 ? newStatuses : undefined);
        },
        [selectedStatuses, onChange],
    );

    return (
        <FormControl size={size} sx={MULTI_SELECT_SX}>
            <InputLabel>{label}</InputLabel>
            <Select
                multiple
                value={selectedStatuses}
                label={label}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                MenuProps={disablePortal ? { disablePortal: true } : undefined}
                renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(status => {
                            const opt = GRADING_STATUS_OPTIONS.find(o => o.value === status);
                            return (
                                <Chip
                                    key={status}
                                    label={opt?.label ?? status}
                                    size="small"
                                    onDelete={handleChipDelete(status)}
                                    onMouseDown={e => e.stopPropagation()}
                                />
                            );
                        })}
                    </Box>
                )}
            >
                {GRADING_STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export const GradingStatusMultiSelect = memo(GradingStatusMultiSelectComponent);

/** Get display label for a grading status */
export function getGradingStatusLabel(status: GradingStatus): string {
    return GRADING_STATUS_OPTIONS.find(o => o.value === status)?.label ?? status;
}

/** Exported for testing and use in chip generation */
export { GRADING_STATUS_OPTIONS };
